/**
 * AI assist controls used by the newsletter and partner forms.
 *
 * Three shapes:
 *  - <AiAssist>      run one prompt — or a chain of them — on a field, preview old vs
 *                    new, then accept or discard.
 *  - <AiExtract>     run an extraction prompt on the newsletter body and append or
 *                    replace the Prayer Requests field with the result.
 *  - <AiFileImport>  read a newsletter out of a PDF or a photo of the printed page.
 *
 * Nothing is written to the record until the user accepts the preview.
 */
import { useState } from 'react'
import { api, useApi, qs, reportClientError } from '../lib/api'
import { HEIC_ACCEPT, isHeic } from '../lib/heic'
import { PromptSelect, ModelSelect } from './Select'
import { ExtractionEngines, enginesSummary } from './Engines'
import { MediaLightbox, stagedPicture } from './ImageUpload'
import { Field, Modal, Spinner, Notice, Badge, useToast, fmtBytes } from './ui'
import { Icon } from './Icon'
import { Dropzone } from './Dropzone'

const stripHtml = (html) =>
  String(html || '')
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\/\s*(p|div|li|h[1-6])\s*>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .trim()

const textToHtml = (text) =>
  String(text || '')
    .split(/\n{2,}/)
    .map((p) => `<p>${p.replace(/\n/g, '<br />')}</p>`)
    .join('')

const escapeHtml = (t) =>
  String(t ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')

/**
 * One-per-line model output -> a bulleted list, which is how requests read best.
 * Any bullet the model already put on the line is dropped, so it is not doubled.
 */
const linesToListHtml = (text) => {
  const items = String(text || '')
    .split('\n')
    .map((l) => l.replace(/^\s*(?:[-–—•*]|\d+[.)])\s+/, '').trim())
    .filter(Boolean)
  if (!items.length) return ''
  return `<ul>${items.map((i) => `<li><p>${escapeHtml(i)}</p></li>`).join('')}</ul>`
}

/** One prompt row: a saved prompt, or a one-off instruction. */
const blankStep = () => ({ promptId: null, modelId: null, instruction: '' })
// Two rows to start with, because the common case is two passes — say, anonymise
// then proofread — and offering the second row up front saves a click every time.
const blankSteps = () => [blankStep(), blankStep()]

const isFilled = (step) => !!step.promptId || !!step.instruction.trim()

/**
 * Run the filled steps in order, each on what the one before it produced. Each step
 * is its own ai/run call, which keeps the audit trail and usage per prompt. `onStep`
 * hears each result as it lands, so a chain that fails partway still has its earlier
 * results on screen; the error is thrown on with the step number in it.
 */
async function runSteps(queue, input, { fieldKey, entity, entityId, onStart, onStep }) {
  const done = []
  for (const [i, step] of queue.entries()) {
    onStart?.(i, queue.length)
    let res
    try {
      res = await api('ai/run', {
        method: 'POST',
        body: {
          prompt_id: step.promptId,
          instruction: step.promptId ? undefined : step.instruction,
          input,
          field_key: fieldKey,
          model_id: step.modelId,
          entity,
          entity_id: entityId,
        },
      })
    } catch (err) {
      throw new Error(
        queue.length > 1 ? `Prompt ${i + 1} of ${queue.length} failed: ${err.message}` : err.message,
        { cause: err }
      )
    }
    done.push(res)
    onStep?.([...done])
    input = res.output
  }
  return done
}

/**
 * The prompt rows: two by default, more on request. An untouched row is skipped
 * when the chain runs, so the second one costs nothing to leave empty.
 */
function PromptSteps({ steps, setSteps, fieldKey, mappedModel, busy, examples = [], allOptional = false }) {
  const patch = (i, p) => setSteps((prev) => prev.map((s, j) => (j === i ? { ...s, ...p } : s)))
  const add = () => setSteps((prev) => [...prev, blankStep()])
  const remove = (i) => setSteps((prev) => (prev.length === 1 ? prev : prev.filter((_, j) => j !== i)))

  return (
    <>
      {steps.map((step, i) => (
        <div className="ai-step" key={i}>
          <div className="row">
            <span className="ai-step-label">Prompt {i + 1}</span>
            {(i > 0 || allOptional) && (
              <span className="small subtle">
                {i === 0 ? 'optional' : `optional · runs on the result of prompt ${i}`}
              </span>
            )}
            <span className="spacer" />
            {steps.length > 1 && (
              <button
                type="button"
                className="btn btn-sm btn-ghost"
                onClick={() => remove(i)}
                disabled={busy}
              >
                Remove
              </button>
            )}
          </div>
          <div className="field-row">
            <PromptSelect
              value={step.promptId}
              onChange={(v) => patch(i, { promptId: v })}
              targetField={fieldKey}
              placeholder={examples[i] ? `Choose a prompt, e.g. ${examples[i]}…` : 'Choose a prompt…'}
            />
            <ModelSelect
              value={step.modelId}
              onChange={(v) => patch(i, { modelId: v })}
              modality="text"
              placeholder={mappedModel ? `${mappedModel.label} (default model)` : 'Default model'}
            />
          </div>
          {!step.promptId && (
            <textarea
              className="textarea"
              value={step.instruction}
              onChange={(e) => patch(i, { instruction: e.target.value })}
              placeholder="…or type an instruction, e.g. Shorten this to three sentences."
              disabled={busy}
              rows={2}
            />
          )}
        </div>
      ))}

      <div className="row">
        <button type="button" className="btn btn-sm" onClick={add} disabled={busy}>
          + Add a Prompt
        </button>
        <span className="small subtle">Empty rows are skipped. Prompts live on AI Settings → Prompts.</span>
      </div>
    </>
  )
}

/**
 * Run a prompt on a field.
 *
 * A run is a list of steps rather than a single prompt. The dialog opens with two
 * rows and "Add a Prompt" puts more below; Run works through the filled ones in
 * order, each reading what the one before it produced. So "anonymise" followed by
 * "proofread" is one trip through the modal instead of two.
 *
 * Each step is a separate ai/run call, which is what keeps the audit trail and the
 * usage figures per prompt. That also means a step can fail with earlier ones already
 * done — when that happens the chain stops and the results so far stay on screen, so
 * the partial work can still be accepted.
 */
export function AiAssist({
  fieldKey,
  fieldLabel,
  value,
  onAccept,
  entity,
  entityId,
  disabled,
  asHtml = true,
  buttonLabel = 'AI assist',
}) {
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [steps, setSteps] = useState(blankSteps)
  // One entry per step that finished, in order; the last one is what gets accepted.
  const [results, setResults] = useState([])
  const [running, setRunning] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const source = asHtml ? stripHtml(value) : String(value || '')
  const final = results.length ? results[results.length - 1] : null
  const showResult = !!final && !busy

  // Every step runs against this same field, so one lookup names the model a step
  // falls back to with no override picked.
  const resolved = useApi(
    ['ai-resolve', fieldKey],
    `ai/resolve${qs({ field_key: fieldKey })}`,
    { enabled: open, staleTime: 60_000 }
  )
  const mappedModel = resolved.data?.ok ? resolved.data.model : null

  const run = async () => {
    const queue = steps.filter(isFilled)
    if (!queue.length) {
      setError('Pick a prompt or type an instruction.')
      return
    }
    setBusy(true)
    setError(null)
    setResults([])
    try {
      await runSteps(queue, source, {
        fieldKey,
        entity,
        entityId,
        onStart: (index, total) => setRunning({ index, total }),
        onStep: setResults,
      })
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(false)
      setRunning(null)
    }
  }

  const accept = () => {
    onAccept(asHtml ? textToHtml(final.output) : final.output)
    toast.ok(`${fieldLabel} replaced with the AI result`)
    close()
  }

  const close = () => {
    setOpen(false)
    setResults([])
    setError(null)
    setRunning(null)
    setSteps(blankSteps())
  }

  const totalMs = results.reduce((n, r) => n + r.ms, 0)
  const filled = steps.filter(isFilled).length
  const reset = () => {
    setResults([])
    setError(null)
  }

  return (
    <>
      <button
        type="button"
        className="btn btn-sm"
        onClick={() => setOpen(true)}
        disabled={disabled || !source}
        title={!source ? `There is no ${fieldLabel.toLowerCase()} to work on yet` : `Run AI on ${fieldLabel}`}
      >
        <Icon name="ai" size={14} /> {buttonLabel}
      </button>

      <Modal
        open={open}
        onClose={close}
        size="xl"
        title={`AI assist — ${fieldLabel}`}
        footer={
          showResult ? (
            <>
              <button className="btn btn-primary" onClick={accept}>
                Use this result
              </button>
              <button className="btn" onClick={reset}>
                Run again
              </button>
              <span className="spacer" />
              <span className="small subtle">
                {results.length > 1
                  ? `${results.length} prompts · ${totalMs} ms`
                  : `${final.model.label} · ${final.ms} ms`}
              </span>
              <button className="btn btn-ghost" onClick={close}>
                Discard
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-primary" onClick={run} disabled={busy}>
                {busy ? (
                  <Spinner
                    label={
                      running && running.total > 1
                        ? `Running prompt ${running.index + 1} of ${running.total}…`
                        : 'Running…'
                    }
                  />
                ) : (
                  `Run${filled > 1 ? ` ${filled} prompts` : ''}`
                )}
              </button>
              <button className="btn" onClick={close} disabled={busy}>
                Cancel
              </button>
            </>
          )
        }
      >
        <div className="col">
          {error && <Notice tone="danger" icon="warning">{error}</Notice>}

          {!showResult && (
            <>
              <PromptSteps
                steps={steps}
                setSteps={setSteps}
                fieldKey={fieldKey}
                mappedModel={mappedModel}
                busy={busy}
                examples={fieldKey === 'newsletters.body_html' ? ['Anonymise', 'Proofread'] : []}
              />

              {results.length > 0 && busy && (
                <Notice tone="info" icon="check">
                  {results.length} prompt{results.length === 1 ? '' : 's'} done so far.
                </Notice>
              )}

              <Field label={`Current ${fieldLabel.toLowerCase()}`}>
                <div className="diff-pane">
                  <div className="diff-body">{source || '—'}</div>
                </div>
              </Field>
            </>
          )}

          {showResult && (
            <>
              <Notice tone="info" icon="info">
                Review before accepting — nothing is saved until you press{' '}
                <strong>Use this result</strong>, and you still have to save the record afterwards.
              </Notice>
              <div className="diff">
                <div className="diff-pane">
                  <div className="diff-head">Before</div>
                  <div className="diff-body">{source}</div>
                </div>
                <div className="diff-pane diff-new">
                  <div className="diff-head">
                    After
                    <Badge tone="accent">
                      {results.length > 1
                        ? `${results.length} prompts`
                        : final.prompt?.name ?? 'ad-hoc'}
                    </Badge>
                  </div>
                  <div className="diff-body">{final.output}</div>
                </div>
              </div>

              {results.length > 1 && (
                <Field
                  label="What each prompt produced"
                  hint="The last one is the After text above."
                >
                  <div className="col">
                    {results.map((r, i) => (
                      <div className="diff-pane" key={i}>
                        <div className="diff-head">
                          Prompt {i + 1}
                          <Badge tone="accent">{r.prompt?.name ?? 'ad-hoc'}</Badge>
                          <span className="spacer" />
                          <span className="diff-meta">
                            {r.model.label} · {r.ms} ms
                          </span>
                        </div>
                        <div className="diff-body">{r.output}</div>
                      </div>
                    ))}
                  </div>
                </Field>
              )}
            </>
          )}
        </div>
      </Modal>
    </>
  )
}

/**
 * Extract prayer requests out of the newsletter body into the Prayer Requests field,
 * in one click: press it and the field holds the extracted list.
 *
 * This was a modal — pick a prompt, choose replace or append, run, read a diff,
 * accept — five interactions for a step that is the same every time. The prompt is
 * the one mapped to newsletters.extract_prayer either way, so the choices were
 * ceremony around a decision nobody varied. It replaces the field rather than asking,
 * because that is what extracting again after editing the update text is for.
 *
 * Nothing reaches the record: this writes to the form, and the record still has to be
 * saved. When the field was not empty the toast carries the old text back on a button,
 * because a click that overwrites work someone typed should be a click to reverse —
 * and the editor's own Undo does not stop at the replacement.
 */
export function AiExtract({ bodyHtml, currentPrayer, onAccept, entityId, disabled }) {
  const toast = useToast()
  const [busy, setBusy] = useState(false)

  const source = stripHtml(bodyHtml)
  const hadRequests = !!stripHtml(currentPrayer)
  // There is no picker on a one-click button, so the prompt it runs — the field's
  // mapped default, or the top of its priority list — is named in the tooltip. It
  // is settable on AI Settings → Prompts, and a button that runs a settable thing should
  // say which one.
  const resolved = useApi(
    ['ai-resolve', 'newsletters.extract_prayer'],
    `ai/resolve${qs({ field_key: 'newsletters.extract_prayer' })}`,
    { staleTime: 60_000 }
  )
  const promptName = resolved.data?.prompt?.name ?? null

  const run = async () => {
    setBusy(true)
    try {
      const res = await api('ai/run', {
        method: 'POST',
        body: {
          input: source,
          field_key: 'newsletters.extract_prayer',
          entity: 'newsletter',
          entity_id: entityId,
        },
      })
      const extracted = res.output.trim()
      if (!extracted) {
        toast.info('No prayer requests found in this update text — the field is unchanged')
        return
      }
      const html = linesToListHtml(extracted)
      const replaced = currentPrayer
      onAccept(html)
      const found = (html.match(/<li>/g) || []).length
      toast.ok(
        `${found} prayer request${found === 1 ? '' : 's'} extracted — remember to save.`,
        hadRequests ? { label: 'Put the old text back', onClick: () => onAccept(replaced) } : null
      )
    } catch (err) {
      toast.error(err)
    } finally {
      setBusy(false)
    }
  }

  return (
    <button
      type="button"
      className="btn btn-sm"
      onClick={run}
      disabled={disabled || busy || !source}
      title={
        source
          ? `Read the update text and ${hadRequests ? 'replace' : 'fill in'} the prayer requests` +
            (promptName ? ` — runs “${promptName}”` : '')
          : 'There is no update text to read yet'
      }
    >
      {busy ? <Spinner label="Reading…" /> : <><Icon name="prayer" size={14} /> Extract prayer requests</>}
    </button>
  )
}

/**
 * Import a newsletter from a PDF or a photograph of the page.
 *
 * The two file kinds are read in genuinely different ways, and the modal says so
 * rather than pretending otherwise:
 *
 *   PDF   the document's own text layer is read, with no model involved at all,
 *         which is exact and instant. A scan — a PDF with pictures of pages and
 *         no text layer — falls back to a vision model reading those pictures.
 *   photo there is no text to convert, so a vision model transcribes the page.
 *         The prompt IS the extraction, and the result is a model's reading of an
 *         image — worth proofreading against the original.
 *
 * Which reader handles a PDF is not asked here. The server already tries them in
 * cost order — text layer, then document conversion, then a vision model — and
 * stops at the first that returns words, so the question only ever asked someone
 * to guess at something the file itself answers. What ran is reported afterwards,
 * in `engines`, which is the part that actually matters: accepting a copy of the
 * document and accepting a model's reading of it are different decisions. The
 * studio (/ai/studio) still offers the choice, for pinning one reader down while
 * tuning a prompt.
 *
 * Two kinds of prompt, kept apart on screen:
 *
 *   reading   how a model transcribes a photo or a scanned PDF. Folded away under
 *             "Reading options", because the field default is right nearly every
 *             time, and a PDF with a text layer never uses it at all.
 *   after     up to two prompts (more on request) run in order on the text once it
 *             has been read — anonymise, then clean up, say. These are the update
 *             text's prompts, each its own ai/run call, chained exactly as AI assist
 *             chains them, so they can be chosen before the file is even read.
 *
 * The "after" prompts replaced a "tidy" checkbox that ran one fixed clean-up on a
 * PDF's text. A clean-up is now just one prompt among those, and it works on a
 * photo's transcription too, which the checkbox never did.
 *
 * Pictures embedded in the file come back alongside the text and are offered as
 * thumbnails, each kept or discarded individually. Keeping one only stages it on
 * the form — the upload happens when the record is saved, because a picture needs
 * a newsletter to belong to and a new newsletter has no id yet.
 *
 * The file itself is never stored; it is read and dropped. Nothing reaches the
 * newsletter until the text is accepted here and the record saved afterwards.
 */
const PDF_MIME = 'application/pdf'
const IMAGE_MIME = ['image/jpeg', 'image/png', 'image/webp', 'image/gif']
const ACCEPT = [PDF_MIME, ...IMAGE_MIME, HEIC_ACCEPT].join(',')

// The classification every prompt written for reading a file is filed under —
// "Text from a photo or PDF scan" on the prompts page. Everything this dialog
// reads is a file, so this is the list it offers, in the order an admin set there.
const IMPORT_FIELD = 'newsletters.import_file'
// Where the clean-up step's own default comes from. Ticking "run the default" sends
// no prompt id, so the server falls back to the field that step names, which is the
// update text rather than this dialog's field — the checkbox has to say so.
const CLEANUP_FIELD = 'newsletters.body_html'

export function AiFileImport({
  currentBody,
  onAccept,
  onPictures,
  entityId,
  maxBytes = 20971520,
  disabled,
}) {
  const toast = useToast()
  const [open, setOpen] = useState(false)
  const [file, setFile] = useState(null)
  // Reading options: the transcription prompt for a photo or a scan.
  const [promptId, setPromptId] = useState(null)
  const [modelId, setModelId] = useState(null)
  const [instruction, setInstruction] = useState('')
  // Prompts run on the text after it is read.
  const [steps, setSteps] = useState(blankSteps)
  const [stepResults, setStepResults] = useState([])
  const [running, setRunning] = useState(null)
  const [mode, setMode] = useState('replace')
  const [result, setResult] = useState(null)
  // Index -> false once a thumbnail is discarded; everything starts kept.
  const [dropped, setDropped] = useState({})
  // The picture being read full size. Keeping or discarding one is a decision made
  // on what is in it, which a 160px thumbnail is not enough to see.
  const [viewing, setViewing] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const isPdf = file?.type === PDF_MIME
  const hasBody = !!stripHtml(currentBody).trim()

  // What reads a photo or a scan when the reading prompt is left empty: the prompt
  // mapped to the field, or failing that the top of its priority list.
  const resolved = useApi(
    ['ai-resolve', IMPORT_FIELD],
    `ai/resolve${qs({ field_key: IMPORT_FIELD })}`,
    { enabled: open, staleTime: 60_000 }
  )
  const defaultPrompt = resolved.data?.prompt ?? null
  const visionModel = resolved.data?.ok ? resolved.data.model : null

  // The model the "after" prompts fall back to.
  const resolvedCleanup = useApi(
    ['ai-resolve', CLEANUP_FIELD],
    `ai/resolve${qs({ field_key: CLEANUP_FIELD })}`,
    { enabled: open, staleTime: 60_000 }
  )
  const textModel = resolvedCleanup.data?.ok ? resolvedCleanup.data.model : null

  const finalText = stepResults.length ? stepResults[stepResults.length - 1].output : result?.text ?? ''

  const pick = (f) => {
    setError(null)
    setResult(null)
    setStepResults([])
    if (!f) return
    if (f.type !== PDF_MIME && !IMAGE_MIME.includes(f.type) && !isHeic(f)) {
      setError(`${f.type || 'That file type'} cannot be imported. Use a PDF, or a JPEG, PNG, WebP, GIF or HEIC image.`)
      return
    }
    if (f.size > maxBytes) {
      setError(`${f.name} is ${fmtBytes(f.size)}; the limit is ${fmtBytes(maxBytes)}.`)
      return
    }
    setFile(f)
  }

  const run = async () => {
    setBusy(true)
    setError(null)
    setResult(null)
    setStepResults([])
    let read
    try {
      const fd = new FormData()
      fd.append('file', file)
      // No pdf_mode: the server's default is the full cascade, which is what this
      // dialog wants every time. The reading prompt goes in the transcription slot
      // only — it is consulted for a photo or a scan, and a PDF with a text layer
      // never reaches a model. Clean-up is the "after" prompts' job now, so no tidy.
      if (promptId) fd.append('ocr_prompt_id', String(promptId))
      else if (instruction.trim()) fd.append('ocr_instruction', instruction.trim())
      if (modelId) fd.append('vision_model_id', String(modelId))
      if (entityId) fd.append('entity_id', String(entityId))
      read = await api('ai/import', { method: 'POST', formData: fd })
      setResult(read)
      setDropped({})
    } catch (err) {
      setError(err.message)
      // Only the failures the Worker never saw — it logs its own, with the reader
      // and the model it got to. This is the file that never arrived.
      reportClientError('import', err, {
        filename: file?.name,
        mime_type: file?.type,
        bytes: file?.size,
        entity: 'newsletter',
        entity_id: entityId ?? null,
      })
      setBusy(false)
      return
    }

    // The text is on screen already; a prompt that fails leaves the results so far
    // standing, so the read text — or a partly processed one — can still be used.
    const queue = steps.filter(isFilled)
    if (queue.length && read.text?.trim()) {
      try {
        await runSteps(queue, read.text, {
          fieldKey: CLEANUP_FIELD,
          entity: 'newsletter',
          entityId,
          onStart: (index, total) => setRunning({ index, total }),
          onStep: setStepResults,
        })
      } catch (err) {
        setError(err.message)
      }
    }
    setRunning(null)
    setBusy(false)
  }

  const pictures = result?.pictures ?? []
  const kept = pictures.filter((_, i) => !dropped[i])

  // "oversize" is not an encoding failure — it decoded fine and was simply too big —
  // so it reads differently from a picture stored in a filter this importer can't
  // lift out at all.
  const skippedReasons = result?.pictures_skipped ?? []
  const oversizeCount = skippedReasons.filter((r) => r === 'oversize').length
  const encodingReasons = [...new Set(skippedReasons.filter((r) => r !== 'oversize'))]
  const encodingCount = skippedReasons.length - oversizeCount

  const accept = () => {
    const text = finalText.trim()
    if (!text && !kept.length) {
      toast.info('Nothing left to import — no text, and every picture was discarded')
      return
    }
    if (text) {
      onAccept(mode === 'append' && hasBody ? `${currentBody}${textToHtml(text)}` : textToHtml(text))
    }
    if (kept.length) onPictures?.(kept.map((p) => ({ ...p, source_name: result.source?.name ?? null })))

    const parts = []
    if (text) parts.push(`${text.length.toLocaleString()} characters`)
    if (kept.length) parts.push(`${kept.length} picture${kept.length === 1 ? '' : 's'}`)
    toast.ok(`Imported ${parts.join(' and ')} — remember to save`)
    close()
  }

  const close = () => {
    setOpen(false)
    setFile(null)
    setResult(null)
    setStepResults([])
    setRunning(null)
    setDropped({})
    setError(null)
    setInstruction('')
    setPromptId(null)
    setModelId(null)
    setSteps(blankSteps())
  }

  return (
    <>
      <button
        type="button"
        className="btn btn-sm"
        onClick={() => setOpen(true)}
        disabled={disabled}
        title="Read the update text out of a PDF or a photo of the page"
      >
        <Icon name="imports" size={14} /> Import from file
      </button>

      <Modal
        open={open}
        onClose={close}
        size="xl"
        title="Import from a PDF or photo"
        footer={
          result && !busy ? (
            <>
              <button className="btn btn-primary" onClick={accept}>
                {finalText.trim()
                  ? mode === 'append'
                    ? `Append text${kept.length ? ` + ${kept.length} picture${kept.length === 1 ? '' : 's'}` : ''}`
                    : `Use text${kept.length ? ` + ${kept.length} picture${kept.length === 1 ? '' : 's'}` : ''}`
                  : `Keep ${kept.length} picture${kept.length === 1 ? '' : 's'}`}
              </button>
              <button
                className="btn"
                onClick={() => {
                  setResult(null)
                  setStepResults([])
                  setError(null)
                }}
              >
                Read again
              </button>
              <span className="spacer" />
              <span className="small subtle">
                {/* Every tool and model that ran, named. This used to fall back to
                    "PDF text layer" whenever `text_source` was missing, which is a
                    guess — and the wrong one for a scan, the file where it matters
                    most that a model did the reading. */}
                {enginesSummary(result.engines) || 'reader not recorded'}
                {stepResults.length ? ` + ${stepResults.length} prompt${stepResults.length === 1 ? '' : 's'}` : ''} ·{' '}
                {result.ms + stepResults.reduce((n, r) => n + r.ms, 0)} ms
              </span>
              <button className="btn btn-ghost" onClick={close}>
                Discard
              </button>
            </>
          ) : (
            <>
              <button className="btn btn-primary" onClick={run} disabled={busy || !file}>
                {busy ? (
                  <Spinner
                    label={
                      running
                        ? `Running prompt ${running.index + 1} of ${running.total}…`
                        : isPdf
                          ? 'Reading the PDF…'
                          : 'Reading the page…'
                    }
                  />
                ) : (
                  'Import'
                )}
              </button>
              <button className="btn" onClick={close} disabled={busy}>
                Cancel
              </button>
            </>
          )
        }
      >
        <div className="col">
          {(!result || busy) && (
            <>
              <Dropzone
                onFiles={(files) => pick(files[0])}
                accept={ACCEPT}
                busy={busy}
                icon={isPdf ? 'file' : file ? 'image' : 'imports'}
                title={file ? `${file.name} (${fmtBytes(file.size)})` : 'Drop a PDF or photo, or click to browse'}
              >
                {file
                  ? isPdf
                    ? 'The text and pictures are read out of the document itself; a scan falls back to a model.'
                    : 'A vision model will transcribe this page.'
                  : `PDF, JPEG, PNG, WebP, GIF or HEIC up to ${fmtBytes(maxBytes)}. The file is not stored.`}
              </Dropzone>

              {file && (
                <>
                  {!isPdf && (
                    <Notice tone="warn" icon="eye">
                      A model will transcribe this picture. Proofread names and numbers against
                      the original before saving.
                    </Notice>
                  )}

                  <details className="ai-reader">
                    <summary>
                      Reading options{' '}
                      <span className="small subtle">
                        {isPdf ? '— only used if this PDF is a scan' : ''}
                        {defaultPrompt && !promptId && !instruction.trim()
                          ? `${isPdf ? '; ' : '— '}default: ${defaultPrompt.name}`
                          : ''}
                      </span>
                    </summary>
                    <div className="field-row">
                      <PromptSelect
                        value={promptId}
                        onChange={setPromptId}
                        targetField={IMPORT_FIELD}
                        placeholder={defaultPrompt ? `${defaultPrompt.name} (default)` : 'Default transcription prompt'}
                      />
                      <ModelSelect
                        value={modelId}
                        onChange={setModelId}
                        modality="vision"
                        placeholder={visionModel ? `${visionModel.label} (default model)` : 'Default model'}
                      />
                    </div>
                    {!promptId && (
                      <textarea
                        className="textarea"
                        value={instruction}
                        onChange={(e) => setInstruction(e.target.value)}
                        placeholder="…or type an instruction, e.g. Transcribe the handwriting, keeping the original spelling."
                        rows={2}
                      />
                    )}
                  </details>
                </>
              )}

              <Field label="Then run on the text">
                <div className="col">
                  <PromptSteps
                    steps={steps}
                    setSteps={setSteps}
                    fieldKey={CLEANUP_FIELD}
                    mappedModel={textModel}
                    busy={busy}
                    examples={['Anonymise', 'Clean up imported text']}
                    allOptional
                  />
                </div>
              </Field>

              {file && (
                <>
                  {hasBody && (
                    <Field label="What to do with the result">
                      <div className="btn-group">
                        <button
                          type="button"
                          className={`btn${mode === 'replace' ? ' active' : ''}`}
                          onClick={() => setMode('replace')}
                        >
                          Replace
                        </button>
                        <button
                          type="button"
                          className={`btn${mode === 'append' ? ' active' : ''}`}
                          onClick={() => setMode('append')}
                        >
                          Append
                        </button>
                      </div>
                    </Field>
                  )}
                </>
              )}

              {error && <Notice tone="danger" icon="warning" title="Import failed">{error}</Notice>}
            </>
          )}

          {result && !busy && (
            <>
              {error && (
                <Notice tone="danger" icon="warning" title="A prompt failed">
                  {error} The text below is what was produced before it.
                </Notice>
              )}
              <Notice tone="info" icon="info">
                Review before accepting — nothing is saved until you press{' '}
                <strong>the button below</strong>, and you still have to save the record afterwards.
              </Notice>
              <div className="diff">
                <div className="diff-pane">
                  <div className="diff-head">Update text now</div>
                  <div className="diff-body">{stripHtml(currentBody) || '— empty —'}</div>
                </div>
                <div className="diff-pane diff-new">
                  <div className="diff-head">
                    Imported
                    {stepResults.length ? (
                      stepResults.map((r, i) => (
                        <Badge tone="accent" key={i}>{r.prompt?.name ?? 'ad-hoc'}</Badge>
                      ))
                    ) : (
                      <Badge tone="accent">{result.prompt?.name ?? (result.kind === 'pdf' ? 'as read' : 'ad-hoc')}</Badge>
                    )}
                  </div>
                  <div className="diff-body">{finalText || '— nothing found —'}</div>
                </div>
              </div>
              {stepResults.length ? (
                <details className="ai-reader">
                  <summary>Text as read, before the prompts</summary>
                  <div className="diff-pane">
                    <div className="diff-body">{result.text}</div>
                  </div>
                </details>
              ) : null}
              <div className="row small subtle">
                <span>{finalText.length.toLocaleString()} characters</span>
                <span aria-hidden="true">·</span>
                <span>{result.source?.name}</span>
                {result.source?.bytes ? (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>{fmtBytes(result.source.bytes)} read, not stored</span>
                  </>
                ) : null}
              </div>

              {/* Named here as well as recorded in the audit log, and from the same
                  list the Worker sent — accepting a transcription is a different
                  decision from accepting a copy, and the person making it should not
                  have to work out which one this was. */}
              <ExtractionEngines engines={result.engines} />

              {pictures.length ? (
                <Field
                  label={`Pictures found in this file — ${kept.length} of ${pictures.length} kept`}
                  hint="Click a picture to see it full size. Discard the ones you do not want — the rest upload when you save the update."
                >
                  <div className="media-grid">
                    {pictures.map((p, i) => {
                      const shot = stagedPicture(
                        p,
                        `Picture ${i + 1} from ${result.source?.name ?? 'the import'}`
                      )
                      return (
                        <div className={`media-item${dropped[i] ? ' is-cleared' : ''}`} key={i}>
                          <button
                            type="button"
                            className="media-open"
                            onClick={() => setViewing(shot)}
                            title="Open full size"
                          >
                            <img
                              className="media-thumb"
                              src={shot.src}
                              alt={shot.alt_text}
                              style={dropped[i] ? { opacity: 0.35 } : undefined}
                            />
                          </button>
                          <div className="media-meta subtle truncate">
                            {p.width && p.height ? `${p.width}×${p.height} · ` : ''}
                            {fmtBytes(p.bytes)}
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() => setDropped((d) => ({ ...d, [i]: !d[i] }))}
                          >
                            {dropped[i] ? 'Keep' : 'Discard'}
                          </button>
                        </div>
                      )
                    })}
                  </div>
                </Field>
              ) : null}

              {result.text_error ? (
                <Notice tone="warn" icon="warning" title="The text could not be read">
                  {result.text_error} The pictures above came through regardless, so you can keep
                  those and type the text yourself.
                </Notice>
              ) : null}

              {skippedReasons.length ? (
                <Notice tone="info" icon="info" title="Some pictures could not be imported">
                  {encodingCount ? (
                    <>
                      {encodingCount} image{encodingCount === 1 ? '' : 's'} {encodingCount === 1 ? 'is' : 'are'}{' '}
                      stored in an encoding this importer cannot lift out directly ({encodingReasons.join(', ')}).
                      It reads JPEG and simple PNG-style pictures directly; the rest would need re-encoding.{' '}
                    </>
                  ) : null}
                  {oversizeCount ? (
                    <>
                      {oversizeCount} image{oversizeCount === 1 ? '' : 's'} {oversizeCount === 1 ? 'was' : 'were'}{' '}
                      too large to import automatically.{' '}
                    </>
                  ) : null}
                  Attach those by hand from the Images section if you need them.
                </Notice>
              ) : null}
            </>
          )}
        </div>
      </Modal>

      {/* A sibling of the import dialog rather than a child of it: the dialog body
          scrolls, and a fixed scrim inside a scrolling box is an easy way to end up
          with a picture that drifts. Modal keeps a stack, so Escape closes this one
          first and leaves the import dialog standing. */}
      <MediaLightbox image={viewing} onClose={() => setViewing(null)} />
    </>
  )
}
